// TeacherArrangement.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import { ref, set, onValue, update, push } from 'firebase/database';
import {
  getTeachers,
  getClasses,
  getSubjects,
  getTimetable,
  updateTeacher,
} from '../services/firebaseService';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const periods = ['1', '2', '3', '4', '5', '6', '7'];
const FIRST_HALF_PERIODS = ['1', '2', '3', '4'];
const SECOND_HALF_PERIODS = ['5', '6', '7'];

const TeacherArrangement = () => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherStatus, setTeacherStatus] = useState({});
  const [arrangements, setArrangements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allTimetables, setAllTimetables] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [saved, setSaved] = useState(false);
  const [teachersWithNoClasses, setTeachersWithNoClasses] = useState([]);

  const today = selectedDate.toISOString().split('T')[0];
  const dayName = days[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1];

  // Load data
  useEffect(() => {
    getTeachers((data) => {
      setTeachers(data || []);
    });

    getClasses((data) => {
      setClasses(data || []);
    });

    getSubjects((data) => {
      setSubjects(data || []);
    });

    loadAllTimetables();
    loadTeacherStatus();
    loadArrangementHistory();
  }, []);

  // Load all timetables and calculate teachers with no classes
  const loadAllTimetables = useCallback(() => {
    getClasses((classesData) => {
      const allTimetablesData = {};
      let loadedCount = 0;

      if (!classesData || classesData.length === 0) {
        setAllTimetables({});
        return;
      }

      classesData.forEach((cls) => {
        const classKey = `${cls.className}_${cls.section}`;
        getTimetable(classKey, (data) => {
          allTimetablesData[classKey] = data || {};
          loadedCount++;

          if (loadedCount === classesData.length) {
            setAllTimetables(allTimetablesData);
            calculateTeachersWithNoClasses(allTimetablesData);
          }
        });
      });
    });
  }, []);

  // Calculate teachers with no weekly classes
  const calculateTeachersWithNoClasses = useCallback((timetablesData) => {
    const teacherSchedule = {};

    teachers.forEach(teacher => {
      teacherSchedule[teacher.name] = 0;
    });

    for (const [className, classTimetable] of Object.entries(timetablesData)) {
      for (const day of days) {
        const dayData = classTimetable[day] || {};
        for (const period of periods) {
          const cell = dayData[period];
          if (cell && cell.teacher) {
            if (teacherSchedule[cell.teacher] !== undefined) {
              teacherSchedule[cell.teacher]++;
            }
          }
        }
      }
    }

    const noClassesTeachers = Object.keys(teacherSchedule).filter(
      teacherName => teacherSchedule[teacherName] === 0
    );

    setTeachersWithNoClasses(noClassesTeachers);
  }, [teachers]);

  // Load teacher status for today
  const loadTeacherStatus = useCallback(() => {
    const statusRef = ref(db, `teacherStatus/${today}`);
    onValue(statusRef, (snapshot) => {
      const data = snapshot.val() || {};
      setTeacherStatus(data);
      if (Object.keys(data).length > 0) {
        generateArrangements(false, data);
      }
    });
  }, [today]);

  // Load arrangement history
  const loadArrangementHistory = useCallback(() => {
    const historyRef = ref(db, `arrangementHistory/${today}`);
    onValue(historyRef, (snapshot) => {
      const data = snapshot.val() || {};
      const historyArray = Object.keys(data).map((key) => ({
        id: key,
        ...data[key]
      }));
      setHistoryData(historyArray);
    });
  }, [today]);

  // Update teacher status
  const updateTeacherStatus = (teacherName, status) => {
    setSaved(false);
    const statusRef = ref(db, `teacherStatus/${today}/${teacherName}`);
    update(statusRef, {
      status: status,
      markedAt: new Date().toLocaleTimeString()
    });
  };

  // Toggle arrangement availability
  const toggleArrangementAvailability = async (teacherId, currentValue) => {
    try {
      const newValue = !currentValue;
      await updateTeacher(teacherId, {
        arrangementAvailable: newValue
      });
      // Update local state
      setTeachers(prevTeachers => 
        prevTeachers.map(t => 
          t.id === teacherId 
            ? { ...t, arrangementAvailable: newValue }
            : t
        )
      );
    } catch (error) {
      console.error('Failed to update arrangement availability:', error);
      alert('Failed to update arrangement availability.');
    }
  };

  // Check if teacher should have arrangement for a specific period based on status
  const shouldGenerateForPeriod = (status, period) => {
    const periodNum = parseInt(period);
    
    if (status === 'Present') return false;
    if (status === 'Absent' || status === 'On Duty') return true;
    if (status === 'Absent - First Half') return periodNum <= 4;
    if (status === 'Absent - Second Half') return periodNum >= 5;
    return true;
  };

  // Get periods for which arrangements should be generated based on status
  const getRelevantPeriods = (status) => {
    if (status === 'Present') return [];
    if (status === 'Absent' || status === 'On Duty') return periods;
    if (status === 'Absent - First Half') return FIRST_HALF_PERIODS;
    if (status === 'Absent - Second Half') return SECOND_HALF_PERIODS;
    return periods;
  };

  // Check if teacher is busy according to timetable
  const isTeacherBusyInTimetable = (teacherName, day, period, excludeClass = null) => {
    for (const [className, classTimetable] of Object.entries(allTimetables)) {
      if (className === excludeClass) continue;
      const dayData = classTimetable[day] || {};
      const cell = dayData[period];
      if (cell && cell.teacher === teacherName) {
        return true;
      }
    }
    return false;
  };

  // Check if teacher is already assigned as substitute in current generation
  const isTeacherAssignedAsSubstitute = (teacherName, day, period, currentArrangements) => {
    return currentArrangements.some(arr => 
      arr.day === day && 
      arr.period === period && 
      arr.substituteTeacher === teacherName
    );
  };

  // Check if teacher is eligible (Present, Active, not Absent, not On Duty)
  const isTeacherEligible = (teacherName) => {
    const status = teacherStatus[teacherName]?.status || 'Present';
    const teacher = teachers.find(t => t.name === teacherName);
    return teacher?.status === 'Active' && 
           status !== 'Absent' && 
           status !== 'On Duty' &&
           status !== 'Absent - First Half' &&
           status !== 'Absent - Second Half';
  };

  // Check if teacher is available for arrangement (must be eligible AND arrangementAvailable === true)
  const isTeacherAvailableForArrangement = (teacherName) => {
    const teacher = teachers.find(t => t.name === teacherName);
    // If arrangementAvailable is explicitly false, they cannot be a substitute
    if (teacher && teacher.arrangementAvailable === false) {
      return false;
    }
    return isTeacherEligible(teacherName);
  };

  // Get teachers with no weekly classes (any subject) - respects arrangementAvailable
  const getTeachersWithNoClasses = (day, period, excludeClass = null, currentArrangements = []) => {
    const eligible = teachersWithNoClasses.filter(teacherName => {
      // Must be available for arrangement
      if (!isTeacherAvailableForArrangement(teacherName)) return false;
      if (isTeacherBusyInTimetable(teacherName, day, period, excludeClass)) return false;
      if (isTeacherAssignedAsSubstitute(teacherName, day, period, currentArrangements)) return false;
      return true;
    });
    return eligible;
  };

  // Get any free teacher (any subject) - respects arrangementAvailable
  const getAnyFreeTeacher = (day, period, excludeClass = null, currentArrangements = []) => {
    const activeTeachers = teachers.filter(t => t.status === 'Active');
    
    const available = activeTeachers.filter(t => {
      // Must be available for arrangement
      if (t.arrangementAvailable === false) return false;
      
      const status = teacherStatus[t.name]?.status || 'Present';
      if (status === 'Absent' || status === 'On Duty' || 
          status === 'Absent - First Half' || status === 'Absent - Second Half') return false;
      if (isTeacherBusyInTimetable(t.name, day, period, excludeClass)) return false;
      if (isTeacherAssignedAsSubstitute(t.name, day, period, currentArrangements)) return false;
      return true;
    });

    return available;
  };

  // Shuffle array (Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Find substitute teacher - respects arrangementAvailable
  const findSubstitute = (absentTeacher, className, subject, day, period, currentArrangements, shuffle = false) => {
    // Priority 1: Teacher with NO weekly classes (any subject)
    const noClassesTeachers = getTeachersWithNoClasses(day, period, className, currentArrangements);
    if (noClassesTeachers.length > 0) {
      const shuffled = shuffle ? shuffleArray(noClassesTeachers) : noClassesTeachers;
      return {
        teacher: shuffled[0],
        reason: 'No Weekly Classes'
      };
    }

    // Priority 2: Any free teacher (any subject) during required day and period
    const anyFreeTeacher = getAnyFreeTeacher(day, period, className, currentArrangements);
    if (anyFreeTeacher.length > 0) {
      const shuffled = shuffle ? shuffleArray(anyFreeTeacher) : anyFreeTeacher;
      return {
        teacher: shuffled[0].name,
        reason: 'Free Teacher'
      };
    }

    // Priority 3: No teacher available
    return {
      teacher: 'No Teacher Available',
      reason: 'No Free Teacher'
    };
  };

  // Generate arrangements (only in memory, not saved to Firebase)
  const generateArrangements = (shuffle = false, statusData = null) => {
    setLoading(true);
    setSaved(false);
    
    const currentStatus = statusData || teacherStatus;
    const newArrangements = [];
    
    // Get all teachers who are absent or on duty (including partial)
    const affectedTeachers = teachers.filter(t => {
      const status = currentStatus[t.name]?.status || 'Present';
      return status !== 'Present';
    });

    if (affectedTeachers.length === 0) {
      setArrangements([]);
      setLoading(false);
      return;
    }

    affectedTeachers.forEach(teacher => {
      const status = currentStatus[teacher.name]?.status || 'Present';
      const relevantPeriods = getRelevantPeriods(status);
      
      // Find all classes this teacher teaches on this day
      for (const [className, classTimetable] of Object.entries(allTimetables)) {
        const dayData = classTimetable[dayName] || {};
        for (const period of periods) {
          // Only process if this period is relevant for this teacher's status
          if (!relevantPeriods.includes(period)) continue;
          
          const cell = dayData[period];
          if (cell && cell.teacher === teacher.name) {
            const subject = cell.subject;
            
            // Pass the current arrangements to check for conflicts
            const substitute = findSubstitute(
              teacher.name, 
              className, 
              subject, 
              dayName, 
              period,
              newArrangements,
              shuffle
            );
            
            const arrangement = {
              day: dayName,
              period: period,
              class: className.replace(/_/g, ' '),
              subject: subject,
              absentTeacher: teacher.name,
              substituteTeacher: substitute.teacher,
              substituteReason: substitute.reason,
              generatedAt: new Date().toLocaleTimeString()
            };
            
            newArrangements.push(arrangement);
          }
        }
      }
    });

    setArrangements(newArrangements);
    setLoading(false);
  };

  // Handle generate with shuffle
  const handleGenerate = () => {
    generateArrangements(false);
  };

  // Handle regenerate with shuffle
  const handleRegenerate = () => {
    generateArrangements(true);
  };

  // Save arrangements to Firebase
  const saveArrangements = () => {
    if (arrangements.length === 0) {
      alert('No arrangements to save. Please generate arrangements first.');
      return;
    }

    setLoading(true);
    
    // Save to arrangements
    const arrangementsRef = ref(db, `arrangements/${today}`);
    set(arrangementsRef, arrangements);
    
    // Save to history
    const historyRef = ref(db, `arrangementHistory/${today}`);
    arrangements.forEach((arr, index) => {
      const refChild = ref(db, `arrangementHistory/${today}/arrangement_${String(index + 1).padStart(3, '0')}`);
      set(refChild, arr);
    });

    // Save teacher attendance
    const attendanceRef = ref(db, `teacherAttendance/${today}`);
    Object.keys(teacherStatus).forEach(teacherName => {
      const status = teacherStatus[teacherName];
      const refChild = ref(db, `teacherAttendance/${today}/${teacherName}`);
      set(refChild, status);
    });

    setSaved(true);
    setLoading(false);
    alert('✅ Arrangements saved successfully!');
  };

  // Format arrangement message for WhatsApp and Copy
  const formatArrangementMessage = () => {
    if (arrangements.length === 0) return 'No arrangements to display.';

    let message = '══════════════════════════════\n';
    message += 'MIS INTERNATIONAL SCHOOL\n';
    message += `Teacher Arrangement\n`;
    message += `Date : ${selectedDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}\n`;
    message += `Day : ${dayName}\n`;
    message += '══════════════════════════════\n\n';

    arrangements.forEach((arr, index) => {
      message += `Period : ${arr.period}\n`;
      message += `Class : ${arr.class}\n`;
      message += `Subject : ${arr.subject}\n`;
      message += `Absent Teacher : ${arr.absentTeacher}\n`;
      message += `Substitute Teacher : ${arr.substituteTeacher}\n`;
      if (index < arrangements.length - 1) {
        message += `\n──────────────────────────────\n\n`;
      }
    });

    message += `\n══════════════════════════════\n`;
    message += `Generated by\n`;
    message += `School Timetable Management System`;

    return message;
  };

  // Copy formatted message
  const copyFormattedMessage = () => {
    const message = formatArrangementMessage();
    navigator.clipboard.writeText(message).then(() => {
      alert('✅ Arrangement copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy. Please try again.');
    });
  };

  // Send via WhatsApp
  const sendWhatsApp = () => {
    if (arrangements.length === 0) {
      alert('No arrangements to send. Please generate arrangements first.');
      return;
    }

    const message = formatArrangementMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Send via WhatsApp Business
  const sendWhatsAppBusiness = () => {
    if (arrangements.length === 0) {
      alert('No arrangements to send. Please generate arrangements first.');
      return;
    }

    const message = formatArrangementMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Status button configuration
  const statusButtons = [
    { key: 'Present', label: 'P', color: '#22c55e', bgColor: '#22c55e', tooltip: 'Present (Full Day)' },
    { key: 'Absent', label: 'A', color: '#ef4444', bgColor: '#ef4444', tooltip: 'Absent (Full Day)' },
    { key: 'Absent - First Half', label: 'FH', color: '#f59e0b', bgColor: '#f59e0b', tooltip: 'Absent First Half (Periods 1–4)' },
    { key: 'Absent - Second Half', label: 'SH', color: '#8b5cf6', bgColor: '#8b5cf6', tooltip: 'Absent Second Half (Periods 5–7)' },
    { key: 'On Duty', label: 'OD', color: '#3b82f6', bgColor: '#3b82f6', tooltip: 'On Duty (Full Day)' },
  ];

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📋 Teacher Arrangement</h1>
      
      <div style={styles.dateSection}>
        <div style={styles.dateCard}>
          <h3 style={styles.dateCardTitle}>{formatDate(selectedDate)}</h3>
          <p style={styles.dateCardDay}>Day: {dayName}</p>
        </div>
      </div>

      {/* Teacher Status Cards */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Teacher Status</h2>
        <div style={styles.teacherGrid}>
          {teachers.map((teacher) => {
            const currentStatus = teacherStatus[teacher.name]?.status || 'Present';
            const hasNoClasses = teachersWithNoClasses.includes(teacher.name);
            const isAvailableForArrangement = teacher.arrangementAvailable !== false;
            
            return (
              <div key={teacher.id} style={styles.teacherCard}>
                <div style={styles.teacherHeader}>
                  <h4 style={styles.teacherName}>
                    {teacher.name}
                    {hasNoClasses && <span style={styles.freeBadge}> 🆓 Free</span>}
                  </h4>
                  <span style={styles.statusLabel}>{currentStatus}</span>
                </div>
                
                <div style={styles.statusButtonsRow}>
                  {statusButtons.map((btn) => {
                    const isSelected = currentStatus === btn.key;
                    return (
                      <button
                        key={btn.key}
                        onClick={() => updateTeacherStatus(teacher.name, btn.key)}
                        style={{
                          ...styles.statusButton,
                          backgroundColor: isSelected ? btn.bgColor : 'white',
                          color: isSelected ? 'white' : btn.color,
                          borderColor: btn.color,
                          fontWeight: isSelected ? '700' : '500',
                        }}
                        title={btn.tooltip}
                      >
                        {btn.label}
                      </button>
                    );
                  })}
                </div>

                {/* Arrangement Toggle Switch */}
                <div style={styles.toggleSection}>
                  <div style={styles.toggleLabel}>
                    <span style={styles.toggleText}>Available For Arrangement</span>
                  </div>
                  <div style={styles.toggleContainer}>
                    <span style={{
                      ...styles.toggleStatusLabel,
                      color: isAvailableForArrangement ? '#16a34a' : '#9ca3af'
                    }}>
                      {isAvailableForArrangement ? 'ON' : 'OFF'}
                    </span>
                    <label style={styles.switch}>
                      <input
                        type="checkbox"
                        checked={isAvailableForArrangement}
                        onChange={() => toggleArrangementAvailability(teacher.id, isAvailableForArrangement)}
                      />
                      <span style={{
                        ...styles.slider,
                        backgroundColor: isAvailableForArrangement ? '#22c55e' : '#d1d5db'
                      }}>
                        <span style={{
                          ...styles.sliderKnob,
                          transform: isAvailableForArrangement ? 'translateX(22px)' : 'translateX(0)'
                        }} />
                      </span>
                    </label>
                  </div>
                  <div style={styles.toggleHint}>
                    <span style={{
                      fontSize: '0.65rem',
                      color: isAvailableForArrangement ? '#16a34a' : '#9ca3af',
                    }}>
                      {isAvailableForArrangement 
                        ? '✅ Can be assigned as substitute' 
                        : '❌ Cannot be assigned as substitute'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Generate Buttons */}
      <div style={styles.buttonSection}>
        <button 
          onClick={handleGenerate} 
          style={styles.generateButton}
          disabled={loading}
        >
          {loading ? 'Generating...' : '🔄 Generate Arrangement'}
        </button>
        <button 
          onClick={handleRegenerate} 
          style={styles.regenerateButton}
          disabled={loading || arrangements.length === 0}
        >
          {loading ? 'Regenerating...' : '♻️ Regenerate Arrangement'}
        </button>
        <button 
          onClick={() => setShowHistory(!showHistory)} 
          style={styles.historyButton}
        >
          {showHistory ? 'Hide History' : '📊 View History'}
        </button>
      </div>

      {/* Arrangements Table - Preview Only */}
      {arrangements.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Arrangement Preview {saved ? '✅ Saved' : '(Not Saved Yet)'}</h2>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Period</th>
                  <th style={styles.th}>Class</th>
                  <th style={styles.th}>Subject</th>
                  <th style={styles.th}>Absent Teacher</th>
                  <th style={styles.th}>Substitute</th>
                  <th style={styles.th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {arrangements.map((arr, index) => (
                  <tr key={index} style={styles.tr}>
                    <td style={styles.td}>{arr.period}</td>
                    <td style={styles.td}>{arr.class}</td>
                    <td style={styles.td}>{arr.subject}</td>
                    <td style={styles.td}>{arr.absentTeacher}</td>
                    <td style={styles.td}>{arr.substituteTeacher}</td>
                    <td style={styles.td}>{arr.substituteReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button 
              onClick={saveArrangements} 
              style={styles.saveButton}
              disabled={loading || saved}
            >
              💾 Save Arrangement
            </button>
            <button 
              onClick={sendWhatsApp} 
              style={styles.whatsappButton}
              disabled={loading}
            >
              📱 WhatsApp
            </button>
            <button 
              onClick={sendWhatsAppBusiness} 
              style={styles.whatsappBusinessButton}
              disabled={loading}
            >
              💼 WhatsApp Business
            </button>
            <button 
              onClick={copyFormattedMessage} 
              style={styles.copyButton}
              disabled={loading}
            >
              📋 Copy
            </button>
          </div>
        </div>
      )}

      {/* History Section */}
      {showHistory && historyData.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Arrangement History</h2>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Day</th>
                  <th style={styles.th}>Period</th>
                  <th style={styles.th}>Class</th>
                  <th style={styles.th}>Subject</th>
                  <th style={styles.th}>Absent Teacher</th>
                  <th style={styles.th}>Substitute</th>
                  <th style={styles.th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((arr, index) => (
                  <tr key={index} style={styles.tr}>
                    <td style={styles.td}>{today}</td>
                    <td style={styles.td}>{arr.day}</td>
                    <td style={styles.td}>{arr.period}</td>
                    <td style={styles.td}>{arr.class}</td>
                    <td style={styles.td}>{arr.subject}</td>
                    <td style={styles.td}>{arr.absentTeacher}</td>
                    <td style={styles.td}>{arr.substituteTeacher}</td>
                    <td style={styles.td}>{arr.substituteReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 20px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '600',
    color: '#0a1e3c',
    marginBottom: '24px',
  },
  dateSection: {
    marginBottom: '30px',
  },
  dateCard: {
    backgroundColor: '#f8faff',
    padding: '16px 20px',
    borderRadius: '12px',
    border: '1px solid #e2e9f2',
  },
  dateCardTitle: {
    margin: 0,
    color: '#0a1e3c',
    fontSize: '1.2rem',
  },
  dateCardDay: {
    margin: '4px 0 0',
    color: '#4c6a8f',
  },
  section: {
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    fontWeight: '500',
    color: '#0a1e3c',
    marginBottom: '16px',
  },
  teacherGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '16px',
  },
  teacherCard: {
    backgroundColor: 'white',
    border: '1px solid #e2e9f2',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  teacherHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    flexWrap: 'wrap',
    gap: '4px',
  },
  teacherName: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#0a1e3c',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  freeBadge: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.65rem',
    fontWeight: '500',
  },
  statusLabel: {
    fontSize: '0.7rem',
    color: '#666',
    backgroundColor: '#f0f4fe',
    padding: '2px 10px',
    borderRadius: '12px',
  },
  statusButtonsRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  statusButton: {
    padding: '6px 12px',
    border: '2px solid',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '36px',
    textAlign: 'center',
    flex: '1 0 auto',
    maxWidth: '60px',
    backgroundColor: 'white',
  },
  toggleSection: {
    borderTop: '1px solid #eef2f7',
    paddingTop: '12px',
    marginTop: '4px',
  },
  toggleLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  toggleText: {
    fontSize: '0.8rem',
    fontWeight: '500',
    color: '#1f3a6b',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  toggleStatusLabel: {
    fontSize: '0.7rem',
    fontWeight: '600',
    minWidth: '28px',
  },
  switch: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '26px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: '0.3s',
    borderRadius: '26px',
    border: '2px solid transparent',
  },
  sliderKnob: {
    position: 'absolute',
    height: '18px',
    width: '18px',
    left: '2px',
    bottom: '2px',
    backgroundColor: 'white',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleHint: {
    marginTop: '4px',
  },
  buttonSection: {
    display: 'flex',
    gap: '16px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  generateButton: {
    padding: '12px 30px',
    backgroundColor: '#0a1e3c',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  regenerateButton: {
    padding: '12px 30px',
    backgroundColor: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  historyButton: {
    padding: '12px 30px',
    backgroundColor: '#f0f4fe',
    color: '#0a1e3c',
    border: '1px solid #d3deed',
    borderRadius: '40px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  th: {
    padding: '12px 16px',
    backgroundColor: '#f8faff',
    textAlign: 'left',
    fontWeight: '600',
    color: '#0a1e3c',
    borderBottom: '2px solid #e2e9f2',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #e9eef5',
  },
  tr: {
    ':hover': {
      backgroundColor: '#f8faff',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  saveButton: {
    padding: '12px 24px',
    backgroundColor: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  whatsappButton: {
    padding: '12px 24px',
    backgroundColor: '#25D366',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  whatsappBusinessButton: {
    padding: '12px 24px',
    backgroundColor: '#128C7E',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
  copyButton: {
    padding: '12px 24px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '40px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: '0.2s',
  },
};

export default TeacherArrangement;